import { ReactNode, useState } from 'react';
import ReactDOM from 'react-dom';
import { animated, useSpring } from 'react-spring';
import styled from 'styled-components';

import useTranslation from 'shared/hooks/useTranslation';

import Image from 'next/image';

type CookiesWrapperProps = {
  isOpen: boolean;
  onClick: () => void;
  children: ReactNode;
  style: { [k: string]: string };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CookiesWrapper = styled(({ isOpen, ...rest }: CookiesWrapperProps) => <animated.div {...rest} />)`
  align-items: center;
  background-color: ${({ theme }) => theme.COLORS.ASH};
  bottom: 0;
  color: black;
  cursor: ${({ isOpen }) => (isOpen ? 'default' : 'pointer')};
  display: flex;
  height: 200px;
  justify-content: center;
  left: 0;
  min-width: 128px;
  position: fixed;
  transform: translateY(75%);
  z-index: 10000;

  ${({ theme }) => theme.MEDIA.MD} {
    min-width: 180px;
  }
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  font-size: 16px;
  left: 16px;
  position: absolute;
  top: 16px;

  .cookie-icon {
    align-items: center;
    display: flex;
    margin-right: 12px;
  }

  ${({ theme }) => theme.MEDIA.MD} {
    font-size: 20px;
    left: 32px;
    top: 12px;
  }
`;

const Text = styled(animated.div)`
  bottom: 32px;
  max-height: 100px;
  overflow-y: scroll;
  opacity: 0;
  padding: 0 8px;
  position: absolute;
  width: 80%;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    border-radius: ${({ theme }) => theme.BORDER_RADIUS};
    background-color: rgba(0, 0, 0, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background-color: #000000;
    outline: none;
    border-radius: ${({ theme }) => theme.BORDER_RADIUS};
  }
`;

const CloseButton = styled(animated.button)`
  background-color: transparent;
  border: none;
  border-radius: 50%;
  color: #000000;
  cursor: pointer;
  font-size: 32px;
  line-height: 1;
  padding: 0 7px;
  position: absolute;
  right: 32px;
  top: 4px;
  transition: background-color 0.3s;

  &:hover,
  &:focus {
    background: rgba(0, 0, 0, 0.05);
  }

  &:focus {
    background: rgba(0, 0, 0, 0.08);
    outline: none;
  }
`;

const STORAGE_KEY = 'TM_PRIVACY_POLICY_ACCEPTED';

function Cookies(): JSX.Element | null {
  const { t } = useTranslation('common');

  const [isVisible, setIsVisible] = useState(() => !localStorage.getItem(STORAGE_KEY) || false);
  const [isOpen, setIsOpen] = useState(false);

  const [{ translateY }, setTranslateY] = useSpring(() => ({ translateY: 75 }));
  const [{ width }, setWidth] = useSpring(() => ({ width: 5 }));
  const [{ opacity }, setOpacity] = useSpring(() => ({ opacity: 0 }));

  function closeText(): void {
    setTranslateY({ translateY: 100 });
    setTimeout(() => {
      setIsVisible(false);
    }, 1000);
    localStorage.setItem(STORAGE_KEY, 'true');
  }

  function openText(): void {
    if (!isOpen) {
      setTranslateY({ translateY: 0 });
      setWidth({ width: 100 });
      setOpacity({ delay: 500, opacity: 1 });
      setIsOpen(true);
    }
  }

  return isVisible
    ? ReactDOM.createPortal(
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <CookiesWrapper
          isOpen={isOpen}
          // eslint-disable-next-line react/jsx-no-bind
          onClick={openText}
          style={{
            transform: translateY?.interpolate((transform) => `translateY(${transform}%)`),
            width: width?.interpolate((w) => `${w}%`),
          }}
        >
          <CloseButton
            // eslint-disable-next-line react/jsx-no-bind
            onClick={closeText}
            style={{ opacity: opacity?.interpolate((o) => o) }}
          >
            &times;
          </CloseButton>
          <Header>
            <div className="cookie-icon">
              <Image width="20" height="20" src="/img/shared/cookie.svg" alt="cookie" lazyBoundary="1000px" />
            </div>
            Cookies
          </Header>
          <Text style={{ opacity: opacity?.interpolate((o) => o) }}>{t('cookies')}</Text>
        </CookiesWrapper>,
        document.body,
      )
    : null;
}

export default Cookies;
